import React from "react";
import { motion } from "framer-motion";
import { Award, GraduationCap, Globe, ArrowRight, Linkedin } from "lucide-react";

const CALENDLY_URL = "https://cal.com/mindways/discovery-call";
const LINKEDIN_URL = "https://www.linkedin.com/in/anirudh101/";
const FOUNDER_PHOTO = "https://customer-assets.emergentagent.com/job_admissions-roadmap/artifacts/35cionsj_Anirudh%20Gupta.jpg";

const credentials = [
  { icon: GraduationCap, text: "MBA, University of Nottingham" },
  { icon: Award, text: "British Council Certified Counsellor" },
  { icon: Award, text: "Certified Career Counsellor" },
  { icon: Globe, text: "First-hand study abroad experience" },
  { icon: Globe, text: "On-ground university visits and insights worldwide" },
];

export default function Founder({ onBookCall }) {
  return (
    <section
      id="founder"
      data-testid="founder-section"
      style={{ padding: "100px 0", backgroundColor: "#fff" }}
    >
      <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 24px" }}>
        <div style={{ gap: "80px" }} className="mw-grid-2">

          {/* Left: Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.15em", textTransform: "uppercase", color: "#F5A000", fontFamily: "'DM Sans', sans-serif" }}>
              Principal Advisor
            </span>
            <h2 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(28px, 4vw, 44px)",
              fontWeight: "700",
              color: "#0F172A",
              margin: "16px 0 8px",
            }}>
              Meet your Principal Advisor
            </h2>
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "24px", color: "#2B2F9E", fontWeight: "600", margin: "0 0 24px", fontStyle: "italic" }}>
              Anirudh Gupta
            </h3>
            <p style={{ fontSize: "17px", lineHeight: "1.8", color: "#475569", fontFamily: "'DM Sans', sans-serif", margin: "0 0 32px" }}>
              Anirudh Gupta is the Founder and Principal Advisor at Mindways. He brings 20+ years of counselling experience, an MBA from the University of Nottingham, and certified counsellor credentials to guide families with clarity and realism.
            </p>

            {/* Credentials */}
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "36px" }}>
              {credentials.map((cred) => {
                const Icon = cred.icon;
                return (
                  <div key={cred.text} style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                    <div style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "8px",
                      backgroundColor: "#EEF0FC",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      <Icon size={16} style={{ color: "#2B2F9E" }} />
                    </div>
                    <span style={{ fontSize: "15px", color: "#0F172A", fontFamily: "'DM Sans', sans-serif", fontWeight: "500" }}>{cred.text}</span>
                  </div>
                );
              })}
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
              <button
                data-testid="founder-book-call-btn"
                onClick={() => window.open(CALENDLY_URL, "_blank")}
                style={{
                  backgroundColor: "#2B2F9E",
                  color: "#fff",
                  border: "none",
                  padding: "16px 32px",
                  borderRadius: "6px",
                  fontSize: "16px",
                  fontWeight: "700",
                  cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  boxShadow: "0 8px 24px rgba(43,47,158,0.25)",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
              >
                Book a call with the Mindways team <ArrowRight size={16} />
              </button>

              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="founder-linkedin-link"
                aria-label="Anirudh Gupta on LinkedIn"
                style={{
                  width: "50px",
                  height: "50px",
                  borderRadius: "8px",
                  backgroundColor: "#EEF0FC",
                  border: "1px solid #CBD5E1",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#2B2F9E",
                  textDecoration: "none",
                  transition: "all 0.2s",
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#2B2F9E"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#EEF0FC"; e.currentTarget.style.color = "#2B2F9E"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <Linkedin size={22} />
              </a>
            </div>
          </motion.div>

          {/* Right: Photo */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{ position: "relative" }}
          >
            {/* Accent decoration */}
            <div style={{
              position: "absolute",
              top: "-20px",
              right: "-20px",
              width: "100%",
              height: "100%",
              borderRadius: "16px",
              border: "2px solid #F5A000",
              opacity: 0.3,
              zIndex: 0,
            }} />

            <div style={{
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 24px 60px rgba(0,0,0,0.12)",
              position: "relative",
              zIndex: 1,
              maxHeight: "520px",
            }}>
              <img
                src={FOUNDER_PHOTO}
                alt="Anirudh Gupta, Founder and Principal Advisor, Mindways"
                style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center", display: "block" }}
              />
            </div>

            {/* 20 years badge */}
            <div style={{
              position: "absolute",
              bottom: "28px",
              left: "-20px",
              zIndex: 10,
              backgroundColor: "#2B2F9E",
              color: "#fff",
              borderRadius: "12px",
              padding: "16px 20px",
              boxShadow: "0 8px 24px rgba(43,47,158,0.35)",
            }}>
              <div style={{ fontSize: "28px", fontWeight: "700", fontFamily: "'Playfair Display', serif" }}>20+</div>
              <div style={{ fontSize: "12px", fontFamily: "'DM Sans', sans-serif", opacity: 0.85 }}>Years of Experience</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
